/* Shared merchandising preferences layered over Shopify's automatic suggestions. */
(() => {
  if (window.WolfRecommendations) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const parse = (node, fallback) => {
    try { return JSON.parse(node?.textContent || ''); } catch (_) { return fallback; }
  };
  const ids = (products) => products.map((product) => String(product.id));

  class Recommendations {
    constructor(element) {
      this.element = element;
      element.dataset.recommendationState = 'loading';
      this.config = parse(element.querySelector('[data-wolf-recommendation-config]'), null);
      this.events = new AbortController();
      this.generation = 0;
      this.cards = new Map();
      this.automaticCards = new Map();
      this.automatic = [];
      this.catalog = [];
      this.preferredIds = [];
      this.rules = [];
      this.cartIds = this.config?.cartIds || [];
      this.grid = element.querySelector('.product-grid');
      this.heading = element.querySelector('.related-products__heading');
      if (!this.config || !this.grid || !window.WolfRecommendationRanking || !window.WolfColors) {
        element.dataset.recommendationFallback = !this.config ? 'configuration' : !this.grid ? 'markup' : 'dependencies';
        this.nativeFallback();
        return;
      }

      const options = { signal: this.events.signal };
      document.addEventListener('change', (event) => {
        if (event.target.closest('variant-radios, variant-selects')?.closest('[data-wolf-product-id]')?.dataset.wolfProductId !== String(this.config.product.id)) return;
        this.recordView();
        this.schedule();
      }, options);
      document.addEventListener('wolf:recommendation-history-change', (event) => {
        if (event.detail?.consentChanged && event.detail?.preferencesAllowed) this.recordView();
        this.schedule();
      }, options);
      document.addEventListener('wolf:fulfillment-change', () => this.schedule(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          // Add-to-cart responses can contain only added lines, not the whole cart.
          this.refreshCart(cartData?.item_count !== undefined ? cartData : null).then(() => this.schedule());
        });
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (data?.sectionId !== this.productSection()?.dataset.section) return;
          this.recordView();
          this.schedule();
        });
      }
      if (this.config.settings.useHistory) {
        window.WolfRecommendationHistory?.init().then(() => {
          if (this.destroyed) return;
          this.recordView();
          this.schedule();
        });
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.start();
      }, { rootMargin: '600px' });
      this.observer.observe(element.closest('.shopify-section') || element);
    }

    productSection() {
      return document.querySelector(`[data-wolf-product-id="${this.config.product.id}"]`);
    }

    selectedVariant() {
      return window.WolfColors.selectedVariant(this.config.product, this.productSection(), this.config.selectedVariantId);
    }

    recordView() {
      if (!this.config.settings.useHistory) return;
      window.WolfRecommendationHistory?.record({
        id: this.config.product.id,
        forms: window.WolfRecommendationRanking.forms(this.config.product),
        color: this.selectedVariant()?.color || '',
      });
    }

    async nativeFallback() {
      try {
        const html = await this.fetchHtml(this.element.dataset.url);
        if (!this.destroyed) {
          this.element.innerHTML = html.querySelector('product-recommendations')?.innerHTML || '';
          this.element.dataset.recommendationState = 'fallback';
        }
      } catch (_) { /* Optional content must not interrupt the product page. */ }
    }

    async fetchHtml(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) throw new Error('Recommendations must use this storefront.');
      const response = await fetch(url.href, { signal: AbortSignal.timeout(8000) });
      if (!response.ok) throw new Error('Recommendations unavailable.');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    async refreshCart(cart) {
      const request = this.cartRequest = (this.cartRequest || 0) + 1;
      try {
        if (!cart) {
          const response = await fetch(`${root()}cart.js`, { signal: AbortSignal.timeout(5000) });
          if (!response.ok) return;
          cart = await response.json();
        }
        if (!this.destroyed && request === this.cartRequest && Array.isArray(cart.items)) {
          this.cartIds = cart.items.map((item) => String(item.product_id));
        }
      } catch (_) { /* Retain the cart IDs provided by Liquid if refresh fails. */ }
    }

    async start() {
      if (this.started || this.destroyed) return;
      this.started = true;
      const automaticTask = (async () => {
        try {
          const url = new URL(this.element.dataset.url, location.origin);
          url.searchParams.set('limit', '10');
          const html = await this.fetchHtml(url.href);
          this.automatic = parse(html.querySelector('[data-wolf-automatic-products]'), []);
          html.querySelectorAll('.product-grid > [data-recommendation-product-id]').forEach((card) => {
            this.automaticCards.set(card.dataset.recommendationProductId, card);
          });
          if (!this.destroyed) this.renderAutomatic();
        } catch (_) { /* Catalog rules can still provide recommendations. */ }
      })();
      const catalogTask = (async () => {
        if (this.config.manualOnly) return;
        try { this.catalog = await window.WolfColors.loadCatalog(`${root()}collections/all`); } catch (_) { /* Keep Shopify fallback. */ }
      })();
      const preferencesTask = (async () => {
        if (this.config.manualOnly) return;
        const forms = window.WolfRecommendationRanking.forms(this.config.product);
        const relevantRules = this.config.rules.filter((rule) => rule.collectionUrl && rule.sources.some((form) => forms.includes(form)));
        const urls = [...new Set([this.config.preferredCollection, ...relevantRules.map((rule) => rule.collectionUrl)].filter(Boolean))];
        const collections = new Map();
        await Promise.allSettled(urls.map(async (url) => {
          collections.set(url, await window.WolfColors.loadCatalog(url));
        }));
        this.preferredProducts = [...collections.values()].flat();
        this.preferredIds = ids(collections.get(this.config.preferredCollection) || []);
        this.rules = relevantRules.flatMap((rule) => rule.sources.map((source) => ({
          source, collectionIds: ids(collections.get(rule.collectionUrl) || []),
        })));
      })();
      await Promise.allSettled([automaticTask, catalogTask, preferencesTask, this.refreshCart()]);
      if (this.destroyed) return;
      this.ready = true;
      this.element.dataset.recommendationState = 'ranking';
      this.schedule();
    }

    products() {
      const products = this.config.manualOnly ? this.config.manualProducts : [
        ...this.automatic, ...this.catalog, ...(this.preferredProducts || []),
        ...this.config.featuredProducts, ...this.config.manualProducts, this.config.product,
      ];
      return [...new Map(products.map((product) => [String(product.id), product])).values()];
    }

    async overlapIds() {
      if (!this.config.settings.avoidSameColor) return [];
      const panel = document.querySelector(`same-color-products[data-product-id="${this.config.product.id}"]`);
      if (!panel) return [];
      try {
        const products = await window.WolfColors.loadCatalog(panel.dataset.collectionUrl);
        return window.WolfColors.matchingProducts(products, this.selectedVariant()?.color, {
          excludeProductIds: [this.config.product.id], limit: Number(panel.dataset.limit),
        }).map(({ product }) => String(product.id));
      } catch (_) { return []; }
    }

    rank(products, sameColorIds = [], limit = this.config.settings.limit) {
      return window.WolfRecommendationRanking.rank({
        products,
        currentProduct: this.config.product,
        currentVariant: this.selectedVariant(),
        manualIds: ids(this.config.manualProducts),
        featuredIds: this.config.manualOnly ? [] : ids(this.config.featuredProducts),
        preferredIds: this.preferredIds,
        shopifyIds: ids(this.automatic),
        cartIds: this.cartIds,
        cartFamilyKeys: [...(this.config.cartFamilies || []), ...this.products()]
          .filter((product) => this.cartIds.map(String).includes(String(product.id)))
          .map((product) => product.familyKey || String(product.id)),
        sameColorIds,
        history: this.config.settings.useHistory ? window.WolfRecommendationHistory?.read() || [] : [],
        settings: { ...this.config.settings, limit },
        rules: this.rules,
      });
    }

    renderAutomatic() {
      const products = this.config.manualOnly
        ? this.automatic.filter((product) => ids(this.config.manualProducts).includes(String(product.id)))
        : this.automatic;
      const cards = this.rank(products).map(({ product }) => this.automaticCards.get(String(product.id))).filter(Boolean);
      if (cards.length) this.render(cards, 'automatic');
    }

    schedule() {
      if (!this.ready || this.destroyed) return;
      this.generation += 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(this.generation), 80);
    }

    async cardFor({ product, variant }) {
      const key = `${product.id}:${variant.id}`;
      if (!this.cards.has(key)) {
        const promise = (async () => {
          const url = new URL(variant.url || product.url, location.origin);
          url.searchParams.set('variant', variant.id);
          url.searchParams.set('section_id', this.element.dataset.sectionId);
          const html = await this.fetchHtml(url.href);
          const card = html.querySelector('[data-wolf-recommendation-card]')?.content.querySelector('[data-recommendation-product-id]');
          if (!card || card.dataset.recommendationProductId !== String(product.id)) throw new Error('Recommendation card missing.');
          return card;
        })();
        this.cards.set(key, promise);
        promise.catch(() => this.cards.delete(key));
      }
      try { return await this.cards.get(key); }
      catch (_) { return this.automaticCards.get(String(product.id)) || null; }
    }

    async update(generation) {
      const sameColorIds = await this.overlapIds();
      if (this.destroyed || generation !== this.generation) return;
      const matches = this.rank(this.products(), sameColorIds, 20);
      this.element.dataset.recommendationState = 'cards';
      const cards = [];
      // Fetch only the cards needed, in batches; failures leave room for fallback candidates.
      for (let offset = 0; offset < matches.length && cards.length < this.config.settings.limit;) {
        const batch = matches.slice(offset, offset + this.config.settings.limit - cards.length);
        offset += batch.length;
        const results = await Promise.allSettled(batch.map((match) => this.cardFor(match)));
        if (this.destroyed || generation !== this.generation) return;
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) cards.push(result.value);
        });
      }
      if (!this.destroyed && generation === this.generation) this.render(cards);
    }

    render(cards, source = 'curated') {
      this.element.dataset.recommendationState = source;
      this.grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));
      this.grid.hidden = !cards.length;
      this.heading.hidden = !cards.length;
      this.element.classList.toggle('product-recommendations--loaded', Boolean(cards.length));
      this.grid.scrollLeft = 0;
      // Scope swatch activation to each new card; global legacy listeners miss later inserts.
      this.grid.querySelectorAll('.variant-swatch').forEach((label) => {
        const activate = (event) => {
          if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
          const card = label.closest('.card');
          if (!card || !label.dataset.wolfVariantUrl) return;
          event.preventDefault();
          event.stopPropagation();
          const variantId = label.getAttribute('for');
          card.querySelectorAll('.media.variant').forEach((media) => {
            media.classList.toggle('visible', media.classList.contains(`variantId-${variantId}`));
          });
          card.querySelectorAll('.variant-swatch').forEach((swatch) => {
            swatch.classList.toggle('visible', swatch === label);
            swatch.setAttribute('aria-pressed', String(swatch === label));
          });
          card.querySelectorAll('a').forEach((link) => { link.href = label.dataset.wolfVariantUrl; });
          const quickAdd = card.querySelector('[data-product-url]');
          if (quickAdd) quickAdd.dataset.productUrl = label.dataset.wolfVariantUrl;
          const price = card.querySelector('.price');
          if (price) {
            price.classList.toggle('price--on-sale', Boolean(label.dataset.wolfVariantCompare));
            price.querySelectorAll('.price__regular .price-item, .price-item--sale').forEach((node) => {
              node.textContent = label.dataset.wolfVariantPrice;
            });
            const compare = price.querySelector('.price__sale .price-item--regular');
            if (compare) compare.textContent = label.dataset.wolfVariantCompare || '';
          }
        };
        label.addEventListener('click', activate, { signal: this.events.signal });
        label.addEventListener('keydown', activate, { signal: this.events.signal });
      });
    }

    destroy() {
      this.destroyed = true;
      this.generation += 1;
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.events.abort();
      this.unsubscribeCart?.();
      this.unsubscribeVariant?.();
    }
  }
  window.WolfRecommendations = { mount: (element) => new Recommendations(element) };
})();
